import React, { useState, useEffect, useRef } from 'react';
import { getSnipers, deleteSniper, startSniper, stopSniper, getSniperStatus } from '../api';
import SniperForm from './SniperForm';
import DeleteModal from './DeleteModal';
import toast from 'react-hot-toast';

const SniperTab = ({ user, token }) => {
  const [snipers, setSnipers] = useState([]);
  const [selectedSniper, setSelectedSniper] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadedSniperId, setLoadedSniperId] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [detailedStatuses, setDetailedStatuses] = useState({});
  const prevStatusesRef = useRef({});

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const playSuccessSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (err) {}
  };

  const triggerNotification = (sniperName) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Queue Joined! 🎉', {
        body: `Sniper "${sniperName}" successfully joined the queue!`,
      });
    }
    playSuccessSound();
  };

  useEffect(() => {
    fetchSnipers();
  }, [user.id]);

  const fetchSnipers = async () => {
    try {
      const data = await getSnipers(user.id);
      setSnipers(data);
      const newStatuses = {};
      const newDetailed = {};
      data.forEach((s) => { 
        newStatuses[s.id] = s.running || false; 
        newDetailed[s.id] = s.detailed_status || 'Stopped';
      });
      setStatuses((prev) => ({ ...prev, ...newStatuses }));
      setDetailedStatuses((prev) => ({ ...prev, ...newDetailed }));
    } catch (err) {
      toast.error('Failed to load snipers');
    }
  };

  const pollStatus = async (id, sniperName) => {
    try {
      const res = await getSniperStatus(id);
      const newStatus = res.detailed_status || (res.running ? 'Running' : 'Stopped');
      setStatuses((prev) => ({ ...prev, [id]: res.running }));
      setDetailedStatuses((prev) => {
        const oldStatus = prev[id];
        if (oldStatus !== 'Stopped - joined the q' && newStatus === 'Stopped - joined the q') {
          triggerNotification(sniperName || 'Config');
        }
        return { ...prev, [id]: newStatus };
      });
    } catch (err) {}
  };

  useEffect(() => {
    if (snipers.length === 0) return;
    const interval = setInterval(() => {
      snipers.forEach((s) => pollStatus(s.id, s.name));
    }, 5000);
    return () => clearInterval(interval);
  }, [snipers]);

  const handleSelect = (sniper) => {
    setSelectedSniper(sniper);
    setEditMode(false);
    if (loadedSniperId && loadedSniperId !== sniper.id) {
      setLoadedSniperId(null);
    }
    pollStatus(sniper.id, sniper.name);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSniper) return;
    try {
      await deleteSniper(selectedSniper.id);
      toast.success('Sniper deleted');
      setSelectedSniper(null);
      setLoadedSniperId(null);
      fetchSnipers();
    } catch (err) {
      toast.error('Failed to delete');
    }
    setShowDeleteModal(false);
  };

  const handleLoadStartStop = async () => {
    if (!selectedSniper) return;

    const isRunningNow = statuses[selectedSniper.id];

    if (isRunningNow) {
      try {
        await stopSniper(selectedSniper.id);
        toast.success('Sniper stopped');
        setStatuses((prev) => ({ ...prev, [selectedSniper.id]: false }));
        setDetailedStatuses((prev) => ({ ...prev, [selectedSniper.id]: 'Stopped - manually' }));
        setLoadedSniperId(null);
      } catch (err) {
        toast.error(err.message || 'Failed to stop');
      }
      return;
    }

    if (loadedSniperId === selectedSniper.id) {
      try {
        await startSniper(selectedSniper.id, user.id, token);
        toast.success('Sniper started');
        setStatuses((prev) => ({ ...prev, [selectedSniper.id]: true }));
        setDetailedStatuses((prev) => ({ ...prev, [selectedSniper.id]: 'Running - starting...' }));
      } catch (err) {
        toast.error(err.message || 'Failed to start');
      }
      return;
    }

    setLoadedSniperId(selectedSniper.id);
    toast.success(`Loaded "${selectedSniper.name}" - click Start to begin`);
  };

  const handleSave = async () => {
    if (!selectedSniper) return;
    setEditMode(true);
    setShowForm(true);
  };

  const getButtonLabel = () => {
    if (!selectedSniper) return 'Load';
    if (statuses[selectedSniper.id]) return 'Stop';
    if (loadedSniperId === selectedSniper.id) return 'Start';
    return 'Load';
  };

  const getButtonClass = () => {
    if (!selectedSniper) return 'btn btn-blurple';
    if (statuses[selectedSniper.id]) return 'btn btn-red';
    if (loadedSniperId === selectedSniper.id) return 'btn btn-green';
    return 'btn btn-blurple';
  };

  return (
    <div className="sniper-tab">
      <div className="sniper-list-panel">
        <div className="panel-header">Snipers</div>
        <div className="sniper-list">
          {snipers.map((sniper) => (
            <div
              key={sniper.id}
              className={`sniper-item ${selectedSniper?.id === sniper.id ? 'selected' : ''}`}
              onClick={() => handleSelect(sniper)}
            >
              <div className="sniper-item-pic">
                {sniper.picture ? <img src={sniper.picture} alt="" /> : sniper.name.charAt(0).toUpperCase()}
              </div>
              <div className="sniper-item-info">
                <div className="sniper-item-name">{sniper.name}</div>
                <div className="sniper-item-status">
                  <div className={`status-indicator ${statuses[sniper.id] ? 'running' : ''}`}></div>
                  {detailedStatuses[sniper.id] || (statuses[sniper.id] ? 'Running' : 'Stopped')}
                </div>
              </div>
            </div>
          ))}
          {snipers.length === 0 && (
            <div style={{ color: 'var(--text-muted)', padding: '24px 16px', textAlign: 'center', fontSize: '13px', lineHeight: '1.5' }}>
              No snipers yet.<br />Click <strong>Create</strong> to add one.
            </div>
          )}
        </div>
      </div>

      <div className="sniper-main">
        <div className="action-bar">
          <button className="btn btn-blurple" onClick={() => { setEditMode(false); setShowForm(true); }}>
            Create
          </button>
          {selectedSniper && (
            <>
              <button className="btn btn-green" onClick={handleSave}>
                Save
              </button>
              <button className={getButtonClass()} onClick={handleLoadStartStop}>
                {getButtonLabel()}
              </button>
              <button
                className={`btn ${selectedSniper ? 'btn-delete-glow' : 'btn-gray'}`}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </button>
            </>
          )}
        </div>

        <div className="config-details">
          {selectedSniper ? (
            <div className="config-card">
              <h2>{selectedSniper.name}</h2>
              {selectedSniper.picture && (
                <div style={{ marginBottom: '16px' }}>
                  <img src={selectedSniper.picture} alt="" style={{ width: '72px', height: '72px', borderRadius: '8px', objectFit: 'cover' }} />
                </div>
              )}
              <div className="config-row">
                <div className="config-row-label">Interval</div>
                <div className="config-row-value">{selectedSniper.click_interval} ms</div>
              </div>
              <div className="config-row">
                <div className="config-row-label">Join Message</div>
                <div className="config-row-value">{selectedSniper.join_message}</div>
              </div>
              <div className="config-row">
                <div className="config-row-label">Queue Full</div>
                <div className="config-row-value">{selectedSniper.queue_full_message}</div>
              </div>
              <div className="config-row">
                <div className="config-row-label">Button</div>
                <div className="config-row-value">{selectedSniper.button_name}</div>
              </div>
              <div className="config-row">
                <div className="config-row-label">Channel ID</div>
                <div className="config-row-value">{selectedSniper.channel_id}</div>
              </div>
              <div className="config-row">
                <div className="config-row-label">Server ID</div>
                <div className="config-row-value">{selectedSniper.server_id}</div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <h3>Select a sniper or create a new one</h3>
              <p>Your sniper configurations will appear here</p>
            </div>
          )}
        </div>
      </div>

      <div className={`form-overlay ${showForm ? 'open' : ''}`}>
        <SniperForm
          userId={user.id}
          existingConfig={editMode ? selectedSniper : null}
          onClose={() => { setShowForm(false); setEditMode(false); }}
          onSave={() => {
            setShowForm(false);
            setEditMode(false);
            fetchSnipers();
          }}
        />
      </div>

      {showDeleteModal && (
        <DeleteModal
          sniperName={selectedSniper?.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default SniperTab;
